import { type ChangeEvent } from 'react';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';

import {
  Box,
  Checkbox,
  Divider,
  InputLabel,
  List,
  Typography,
} from '@mui/material';
import { UploadItem } from './UploadItem';

import type { TabPanelProps } from './vectorModal';

interface EditStoredFilesTabPanelProps extends TabPanelProps {}

export function EditStoredFilesTabPanel({
  selectedTab,
}: EditStoredFilesTabPanelProps) {
  const { storedFiles, selectedStoredFiles, onSelectedStoredFilesChange } =
    useVectorModal();

  const handleSelectAllStoredFiles = (evt: ChangeEvent<HTMLInputElement>) => {
    const isChecked = evt.target.checked;

    if (isChecked) {
      const newSelectedFilesList = storedFiles.map(({ id }) => id);

      onSelectedStoredFilesChange(newSelectedFilesList);
    } else {
      onSelectedStoredFilesChange([]);
    }
  };

  const handleSelectedStoredFilesToggle = (fileId: string) => {
    if (selectedStoredFiles.includes(fileId)) {
      const newSelectedFilesList = selectedStoredFiles.filter(
        id => id !== fileId,
      );

      onSelectedStoredFilesChange(newSelectedFilesList);
      return;
    }

    onSelectedStoredFilesChange([...selectedStoredFiles, fileId]);
  };

  const isAllStoredFilesSelected =
    selectedStoredFiles.length > 0 &&
    selectedStoredFiles.length === storedFiles.length;
  const isIndeterminateStoredFilesSelected =
    selectedStoredFiles.length > 0 &&
    selectedStoredFiles.length < storedFiles.length;

  const disableSelectAllStoredFiles = storedFiles.length === 0;

  return (
    <Box
      role="tabpanel"
      hidden={selectedTab !== 'edit-stored-files'}
      id="vertical-tabpanel-edit-stored-files"
      aria-labelledby="vertical-tab-edit-stored-files"
      sx={{
        width: '100%',
        '&>*+*': {
          marginBlockStart: '2rem !important',
        },
        '&[hidden]': {
          display: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: '900px',
          marginInline: 'auto',
          paddingBlockStart: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h2"
          fontWeight={500}
          fontSize={20}
          sx={{ lineHeight: '120%' }}
        >
          Edit Stored Files
        </Typography>

        <InputLabel
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            color: 'black',
          }}
        >
          <Checkbox
            checked={isAllStoredFilesSelected}
            indeterminate={isIndeterminateStoredFilesSelected}
            onChange={handleSelectAllStoredFiles}
            disabled={disableSelectAllStoredFiles}
            id="stored-select-all"
            sx={{ padding: 0 }}
          />

          <span
            style={{ opacity: disableSelectAllStoredFiles ? '50%' : '100%' }}
            aria-disabled={disableSelectAllStoredFiles}
          >
            Select All
          </span>
        </InputLabel>
      </Box>

      <Divider />

      <Box
        sx={{
          width: '900px',
          marginInline: 'auto',
          '&>*+*': {
            marginBlockStart: '2rem !important',
          },
        }}
      >
        <List sx={{ '&>*+*': { marginBlockStart: '1.25rem' } }}>
          {storedFiles.map(
            ({
              chunkingMethod,
              fileName,
              id,
              metadatas,
              dimensions,
              status,
            }) => (
              <UploadItem
                key={id}
                checked={selectedStoredFiles.includes(id)}
                chunking={chunkingMethod}
                fileName={fileName}
                metadatas={metadatas}
                dimensions={dimensions}
                id={id}
                status={status}
                onSelectedFilesChange={handleSelectedStoredFilesToggle}
              />
            ),
          )}
        </List>
      </Box>
    </Box>
  );
}
